import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import querystring from "query-string";
import { api } from "@/src/core/api/client";
import { paginationService } from "@/src/shared/services/pagination";
import type { IPaginatedNotifications } from "../types";

const PAGE_SIZE = 20;

const notificationKeys = {
  all: ["notifications"] as const,
  page: (userId: number | undefined, page: number) =>
    [...notificationKeys.all, "page", userId ?? "anon", page] as const,
  infinite: (userId: number | undefined) =>
    [...notificationKeys.all, "infinite", userId ?? "anon"] as const,
};

/** Raw calls (Laravel). Throw an ApiError on failure. */
const notificationsApi = {
  async getPage(
    page: number,
    limit = PAGE_SIZE,
  ): Promise<IPaginatedNotifications> {
    const { data } = await api.laravel.get<IPaginatedNotifications>(
      `/notification?${querystring.stringify({ limit, page })}`,
    );
    return data;
  },
};

/** One page of notifications (desktop list, header badge). */
export const useNotificationsPageQuery = (
  userId: number | undefined,
  page: number,
  enabled = true,
) =>
  useQuery({
    queryKey: notificationKeys.page(userId, page),
    queryFn: () => notificationsApi.getPage(page),
    enabled,
    placeholderData: keepPreviousData,
  });

/** All notifications, loaded page by page as the user scrolls (mobile). */
export const useNotificationsInfiniteQuery = (userId: number | undefined) =>
  useInfiniteQuery({
    queryKey: notificationKeys.infinite(userId),
    queryFn: ({ pageParam }) => notificationsApi.getPage(pageParam),
    initialPageParam: 1,
    getNextPageParam: paginationService.nextPage,
  });
